import { useTranslation } from "@sagot/i18n";
import { Button, Icon, Input, Select } from "@sagot/ui";
import { isFiltering, LISTED_AREAS, NO_FILTERS, type KindFilter, type ListedArea, type ListFilters, type StateFilter } from "./defaults-model";

const D = "engage.workflows.defaults";
const F = `${D}.service.filters`;
const STATES: readonly Exclude<StateFilter, "">[] = ["live", "paused", "off", "modified"];
const KINDS: readonly Exclude<KindFilter, "">[] = ["facing", "background"];

interface DefaultsToolbarProps {
  filters: ListFilters;
  /** The areas that have defaults: only they are offered. */
  areas: readonly ListedArea[];
  shown: number;
  total: number;
  onChange: (filters: ListFilters) => void;
}

/** Search and filters of the default workflows: name, trigger or WD number; state; kind; area. The count says how many of them show. */
export function DefaultsToolbar({ filters, areas, shown, total, onChange }: DefaultsToolbarProps) {
  const { t } = useTranslation();
  const set = (patch: Partial<ListFilters>) => { onChange({ ...filters, ...patch }); };
  return (
    <div className="space-y-2" role="search" aria-label={t(`${F}.label`)} data-testid="defaults-toolbar">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 basis-full sm:flex-1 sm:basis-64">
          <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-subtle" />
          <Input type="search" className="pl-9" autoComplete="off" aria-label={t(`${F}.searchLabel`)} placeholder={t(`${F}.search`)} value={filters.q} onChange={(e) => { set({ q: e.target.value }); }} />
        </div>
        <Select className="h-10 w-auto min-w-0 max-w-full" aria-label={t(`${F}.state`)} value={filters.state} onChange={(e) => { set({ state: e.target.value as StateFilter }); }}>
          <option value="">{t(`${F}.anyState`)}</option>
          {STATES.map((state) => <option key={state} value={state}>{t(`${F}.${state}`)}</option>)}
        </Select>
        <Select className="h-10 w-auto min-w-0 max-w-full" aria-label={t(`${F}.kind`)} value={filters.kind} onChange={(e) => { set({ kind: e.target.value as KindFilter }); }}>
          <option value="">{t(`${F}.anyKind`)}</option>
          {KINDS.map((kind) => <option key={kind} value={kind}>{t(`${F}.${kind}`)}</option>)}
        </Select>
        {areas.length > 1 ? (
          <Select className="h-10 w-auto min-w-0 max-w-full" aria-label={t(`${F}.area`)} value={filters.area} onChange={(e) => { set({ area: e.target.value as ListFilters["area"] }); }}>
            <option value="">{t(`${F}.anyArea`)}</option>
            {LISTED_AREAS.filter((area) => areas.includes(area)).map((area) => <option key={area} value={area}>{t(area === "orders_field_service" ? `${D}.service.areas.${area}` : `${D}.areas.${area}`)}</option>)}
          </Select>
        ) : null}
      </div>
      <div className="flex min-h-8 flex-wrap items-center gap-3 text-xs text-fg-subtle" aria-live="polite">
        {isFiltering(filters) ? (
          <>
            <span data-testid="defaults-count">{t(`${F}.count`, { shown, total })}</span>
            <Button type="button" variant="ghost" size="sm" onClick={() => { onChange(NO_FILTERS); }}>
              <Icon name="x" />
              {t(`${F}.clear`)}
            </Button>
          </>
        ) : null}
      </div>
    </div>
  );
}
